import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { name: 'Home', href: '#home', path: '/' },
    { name: 'Research', href: '#research', path: '/' },
    { name: 'PI', href: '/pi', path: '/pi' },
    { name: 'Team', href: '/team', path: '/team' },
    { name: 'P.R.I.S.M.', href: '/logic', path: '/logic' },
    { name: 'Publications', href: '#publications', path: '/' },
    { name: 'Funding', href: '#funding', path: '/' },
    { name: 'Join Us', href: '#recruitment', path: '/' },
    { name: 'Collaborators', href: '#collaborators', path: '/' },
    { name: 'Contact', href: '#contact', path: '/' }
  ];

  const handleNavigation = (e, item) => {
    e.preventDefault();
    setIsMobileMenuOpen(false);
    
    if (item.path === '/team' || item.path === '/pi' || item.path === '/logic') {
      navigate(item.path);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const element = document.querySelector(item.href);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      const element = document.querySelector(item.href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'glass-light shadow-lg shadow-indigo-500/10' 
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-5 flex-nowrap gap-4">
          <div className="flex items-center">
            <button 
              onClick={() => {
                navigate('/');
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              className="text-2xl font-bold cursor-pointer py-2 whitespace-nowrap" 
              style={{ fontFamily: "'Space Grotesk', sans-serif", minHeight: '44px' }}
            >
              <span className={`transition-colors duration-300 ${
                isScrolled ? 'gradient-text-primary' : 'text-white'
              }`}>
                Chaturvedi Lab
              </span>
            </button>
          </div>

          {/* Desktop Navigation - Compact for single line */}
          <nav className="hidden lg:flex items-center space-x-4 xl:space-x-6 flex-shrink-0">
            {navItems.map((item) => (
              <button
                key={item.name}
                onClick={(e) => handleNavigation(e, item)}
                className={`text-sm xl:text-base font-semibold transition-colors duration-300 hover:text-indigo-500 py-3 px-1.5 xl:px-2 whitespace-nowrap ${
                  isScrolled ? 'text-slate-700' : 'text-white/90'
                }`}
                style={{ minHeight: '44px' }}
              >
                {item.name}
              </button>
            ))}
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2.5 flex-shrink-0"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            style={{ minWidth: '44px', minHeight: '44px' }}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? (
              <X className={`h-6 w-6 ${isScrolled ? 'text-slate-900' : 'text-white'}`} />
            ) : (
              <Menu className={`h-6 w-6 ${isScrolled ? 'text-slate-900' : 'text-white'}`} />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden glass-light shadow-lg border-t border-indigo-100">
          <nav className="px-4 pt-2 pb-4 space-y-2">
            {navItems.map((item) => (
              <button
                key={item.name}
                onClick={(e) => handleNavigation(e, item)}
                className="block w-full text-left py-3 px-4 text-slate-700 hover:text-indigo-600 font-semibold transition-colors"
                style={{ minHeight: '48px' }}
              >
                {item.name}
              </button>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
